import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { CompanyModel } from 'src/app/core/models/CompanyModel';
import { User } from 'src/app/core/models/user';
import { NzMessageService } from 'ng-zorro-antd/message';
import { UserService } from 'src/app/core/services/user.service';
import { AdminService } from '../admin.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DbMasterService } from 'src/app/core/services/db-master.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DeactivateTypeEnum } from 'src/app/core/models/DeactivateTypeEnum';
import { ViewValidationsComponent } from '../view-validations/view-validations.component';

@Component({
  selector: 'app-company',
  standalone: false,
  templateUrl: './company.component.html',
  styleUrl: './company.component.less'
})
export class CompanyComponent {
  companyForm!: FormGroup; // Reactive form instance
  isVisible = false; // Modal visibility
  isConfirmLoading = false; // Loading spinner for modal actions
  isEditMode = false; // Determines if the form is in edit mode
  exportData: any[] = [];
  searchText: string = "";

  companyMaster: CompanyModel[] = []; // List of companies
  companyToBeEdited = new CompanyModel(); // Company being edited
  companyToBeAdded = new CompanyModel(); // New company to be added
  duplicateCompErrMsg: string = ''; // Error message for duplicate company name
  duplicateAcronymErrMsg: string = ''; // Error message for duplicate acronym

  openQueries = [];
  currentUser: User = new User();
  constructor(
    private router: Router,
    private messageService: NzMessageService,
    private userService: UserService,
    private adminService: AdminService,
    private modalService: NzModalService,
    private dbMasterService: DbMasterService,
    private fb: FormBuilder,
  ) { }

  ngOnInit() {
  
    this.currentUser = this.userService.getCurrentUser();
    
    if (this.currentUser?.User_Code == null) {      
      window.location.href = '/';
    }
    if (this.currentUser.IsRFCAdmin) {
      this.getAllCompanies();
      this.initializeCompanyForm(); // Initialize the form
      this.companyToBeAdded.Company_Code = 0; // Default value for new company
    }
    else {
      this.router.navigate(['/unauthorize']);
    }
  }

  getAllCompanies() {
    this.adminService.getAllCompany().subscribe((response: any) => {
      this.companyMaster = response.output;
      this.companyMaster.forEach(function (element: any) {
        element.CompStatus = element["Active"] ? "Active": "Inactive";
      });
    });
  }
  // Initialize the reactive form
  initializeCompanyForm(): void {
    this.companyForm = this.fb.group({
      companyName: ['', [Validators.required, Validators.maxLength(50)]],
      companyAcronym: ['', [Validators.required, Validators.maxLength(3)]]
    });
  }
  // Show the modal for creating a new company
  showCreateModal(): void {
    this.isEditMode = false;
    this.companyForm.reset();
    this.isVisible = true;
  }

  // Show the modal for editing an existing company
  showEditModal(company: CompanyModel): void {
    this.isEditMode = true;
    this.companyToBeEdited = { ...company }; // Create a copy of the company object
    this.companyForm.patchValue({
      companyName: company.Company_Name,
      companyAcronym: company.Company_Acronym
    });
    this.isVisible = true;
  }

  // Handle the modal's OK button
  handleOk(): void {
    if (this.companyForm.valid) {
      this.isConfirmLoading = true;
      const formValue = this.companyForm.value;

      if (this.isEditMode) {
        this.companyToBeEdited.Company_Name = formValue.companyName;
        this.companyToBeEdited.Company_Acronym = formValue.companyAcronym;
        this.updateCompany();
      } else {
        this.companyToBeAdded.Company_Name = formValue.companyName;
        this.companyToBeAdded.Company_Acronym = formValue.companyAcronym;
        this.addCompany();
      }
    }
  }

  // Handle the modal's Cancel button
  handleCancel(): void {
    this.isVisible = false;
    this.companyForm.reset();
    this.isEditMode = false;
  }

  // Add a new company
  addCompany(): void {
    const duplicateName = this.companyMaster.some(
      (company) =>
        company.Company_Name.toLowerCase().trim() ===
        this.companyToBeAdded.Company_Name.toLowerCase().trim()
    );
    const duplicateAcronym = this.companyMaster.some(
      (company) =>
        company.Company_Acronym.toLowerCase().trim() ===
        this.companyToBeAdded.Company_Acronym.toLowerCase().trim()
    );

    if (duplicateName) {
      this.duplicateCompErrMsg = 'Company Name already exists.';
      this.isConfirmLoading = false;
      return;
    }

    if (duplicateAcronym) {
      this.duplicateAcronymErrMsg = 'Company Acronym already exists.';
      this.isConfirmLoading = false;
      return;
    }

    this.companyToBeAdded.Created_By = this.currentUser.User_Code ?? 0;
    this.adminService.addCompany(this.companyToBeAdded).subscribe((response: any) => {
      if (response.result) {
        this.messageService.success(
          `Company ${this.companyToBeAdded.Company_Name} created successfully!`
        );
        this.getAllCompanies();
        this.resetModalPopup();
        this.handleCancel();
      }
      this.isConfirmLoading = false;
    });
  }

  // Update an existing company
  updateCompany(): void {
    const duplicateName = this.companyMaster.some(
      (company) =>
        company.Company_Name.toLowerCase().trim() ===
          this.companyToBeEdited.Company_Name.toLowerCase().trim() &&
        company.Company_Code !== this.companyToBeEdited.Company_Code
    );
    const duplicateAcronym = this.companyMaster.some(
      (company) =>
        company.Company_Acronym.toLowerCase().trim() ===
          this.companyToBeEdited.Company_Acronym.toLowerCase().trim() &&
        company.Company_Code !== this.companyToBeEdited.Company_Code
    );

    if (duplicateName) {
      this.duplicateCompErrMsg = 'Company Name already exists.';
      this.isConfirmLoading = false;
      return;
    }

    if (duplicateAcronym) {
      this.duplicateAcronymErrMsg = 'Company Acronym already exists.';
      this.isConfirmLoading = false;
      return;
    }
    
    this.companyToBeEdited.Created_By = this.currentUser.User_Code ?? 0;
    this.adminService.updateCompany(this.companyToBeEdited).subscribe({
      next: (response: any) => {
        if (response.result) {
          this.messageService.success(
            `Company ${this.companyToBeEdited.Company_Name} updated successfully!`
          );
          this.getAllCompanies();
          this.handleCancel();
          this.resetModalPopup();
        }
        this.isConfirmLoading = false;
      },
      error: (error) => {
        console.error('Error updating company:', error);
        this.messageService.error('An error occurred while updating the company. Please try again later.');
        this.isConfirmLoading = false;
      }
    });
  }

  // Deactivate a company
  showDeactivateModal(company: CompanyModel): void {
    this.modalService.confirm({
      nzTitle: 'Confirm!',
      nzContent: `Are you sure you want to deactivate the company - <strong>${company.Company_Name}</strong>?`,
      nzOkText: 'Yes',
      nzOkType: 'primary',
      nzClosable: false,
      nzOkDanger: true,
      nzOnOk: () => this.deactivateCompany(company),
      nzCancelText: 'No'
    });
  }

  deactivateCompany(company: CompanyModel): void {
    this.dbMasterService.GetActiveQueryReferenceNumber(DeactivateTypeEnum.Company,company.Company_Code).subscribe((response: any) => {
      if (response.output.length == 0) {
        let selectedCompany = new CompanyModel();
        selectedCompany.Company_Code = company.Company_Code;
        selectedCompany.Created_By = this.currentUser.User_Code??0;
        this.adminService.deleteCompany(selectedCompany).subscribe((response: any) => {
          if (response.result) {
            this.messageService.success(
              `Company ${company.Company_Name} deactivated successfully!`
            );
            this.getAllCompanies();
            this.resetModalPopup();
          }
          else{
            this.messageService.error("Only active Company can be deactivated.");
          }
          this.resetToDefault();
        });
      }
      else{
        this.openQueries = response.output as []; 
        this.viewValidationMsg();
      }
    });

  }

 resetToDefault() {
    this.companyToBeAdded = this.companyToBeEdited =  new CompanyModel(); 
    this.companyToBeAdded.Company_Code = 0;
    this.duplicateCompErrMsg = this.duplicateAcronymErrMsg = "";
    this.openQueries = [];
  }
  viewValidationMsg() {    
      this.modalService.create({
            nzTitle: 'Validations',
            nzClosable: false,
            nzFooter: null,
            nzAutofocus: null,
            nzContent: ViewValidationsComponent,
            nzData: { 
                      validationText: 'Please respond to the open queries and then only deactivate <b>Department</b>.', 
                      openQueries: this.openQueries 
                    },
          });
    }
  resetModalPopup() {
    this.resetToDefault(); // Reset the form and messages
    this.isVisible = false; // Hide the modal
    this.isEditMode = false; // Reset to create mode
    this.isConfirmLoading = false; // Hide loading spinner
    this.modalService.closeAll(); // Close any open modals
  }

  listOfColumn = [      
        {
          title: 'Company',
          compare: (a: CompanyModel, b: CompanyModel) => (a.Company_Name || '').localeCompare(b.Company_Name || ''),
          priority: 3,
          allowSorting: true
        },        
        {
          title: 'Acronym',
          compare: (a: CompanyModel, b: CompanyModel) => (a.Company_Acronym || '').localeCompare(b.Company_Acronym || ''),
          priority: 1,
          allowSorting: true
        },        
        {
          title: 'Status',
          compare: null,
          priority: 1,
          allowSorting: true
        },
        {
          title: 'Action',
          compare: null,
          priority: false,
          allowSorting: false
        }
      ];
}
